import {
  CalendarBlank,
  CaretLeft,
  Drop,
  PencilSimple,
  TrashSimple,
} from '@phosphor-icons/react'
import { Button } from 'antd'
import moment from 'moment'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { EditableBadge } from '@/components/Badges/EditableBadge'
import { EditableBadgeItemProps } from '@/components/Badges/EditableBadge/EditableBadgeItem'
import { Skeleton } from '@/components/Skeleton'
import { CustomerModel } from '@/domain/model/customer'
import { QUERIES } from '@/enums/queries'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import {
  invalidLoadCustomerById,
  useLoadCustomer,
} from '@/queries/customers/useLoadCustomer'
import { useLoadTagsClient } from '@/queries/customers/useLoadTagsClient'
import { useUpdateCustomerById } from '@/queries/customers/useUpdateCustomerById'
import { globalQueryClient } from '@/queries/queryClient'
import { getInitialsName } from '@/utils/getInitialsName'

import { AddBadgeButtonClient } from './AddBadgeButtonClient'
import { ClientHeaderSettings } from './ClientHeaderSettings'
import { AddTagClientModal } from './ClientTagModal'
import { ConfirmDeleteTagClientModal } from './ClientTagModal/ConfirmDeleteTagClientModal'
import { EditTagClientModal } from './ClientTagModal/EditTagClientModal'

export const ClientHeader = () => {
  const history = useHistory()
  const { clientId } = useParams<{ clientId: string }>()
  const searchParams = useSearchParams()
  const updateParams = useUpdateURLSearchParams()

  const isOpenCreateTagModal = searchParams.get('createTag') === 'open'
  const isOpenEditTagModal = searchParams.get('editTag')
  const isOpenDeleteTagModal = searchParams.get('deleteTag')

  const { data: clientDetail, isLoading } = useLoadCustomer(clientId)
  const { updateCustomerById } = useUpdateCustomerById()
  const { data: badgeData = [] } = useLoadTagsClient()

  const clientDetailData = clientDetail as CustomerModel

  const tagList = (clientDetailData?.customer_tags || []).map(
    (item) => badgeData.find((tag) => tag.id === item.id) || item
  )

  const dateOfBirth = clientDetailData?.date_birth
    ? moment(clientDetailData?.date_birth).format('MM/DD/YYYY')
    : 'Empty'

  const removeTagFromClient = async (tagId: string) => {
    const removeTagById = clientDetailData?.customer_tags?.filter(
      (item) => item?.id !== tagId
    )
    try {
      globalQueryClient.setQueryData<CustomerModel>(
        [QUERIES.CUSTOMERS.GET, clientId],
        (old) => {
          if (!old) return old
          return {
            ...old,
            customer_tags: removeTagById,
          }
        }
      )
      await updateCustomerById({ id: clientId, customer_tags: removeTagById })
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      invalidLoadCustomerById(clientId)
    } catch (error) {
      globalQueryClient.setQueryData<CustomerModel>(
        [QUERIES.CUSTOMERS.GET, clientId],
        (old) => {
          if (!old) return old
          return {
            ...old,
            customer_tags: (old.customer_tags || []).filter(
              (item) => item.id !== tagId
            ),
          }
        }
      )
      toast.error('Error trying to remove tag from patient')
    }
  }

  const BadgeEditList: EditableBadgeItemProps['badgeList'] = [
    {
      id: 'delete',
      content: (
        <span className="flex items-center gap-1 text-red-700">
          <TrashSimple className="text-base" weight="bold" />
          Delete
        </span>
      ),
      onClick: (tagId) => removeTagFromClient(tagId),
    },
    {
      id: 'rename',
      content: (
        <span className="flex items-center gap-1">
          <PencilSimple className="text-base" weight="bold" />
          Rename
        </span>
      ),
      onClick: (id: string) => updateParams(['editTag', id]),
    },
    {
      id: 'change-color',
      content: (
        <span className="flex items-center gap-1">
          <Drop className="text-base" weight="bold" />
          Change color
        </span>
      ),
      onClick: (id: string) => updateParams(['editTag', id]),
    },
  ]

  return (
    <>
      <div className="h-44 w-full border-b border-b-gray-200 bg-white">
        <div className="mx-auto max-w-screen-xl md:px-3">
          <div className="flex w-full pt-1">
            <Button
              className="!p-3 text-sm !text-zentake !outline-none hover:!text-zentake/60"
              onClick={() => history.push('/clients')}
              type="link"
            >
              <CaretLeft weight="bold" /> Back
            </Button>
          </div>
          <div className="mt-2.5 flex items-center justify-start gap-4">
            <Skeleton
              {...{ isLoading }}
              className="!h-16 !min-h-16 !w-16 !min-w-[64px] rounded-full"
            >
              <span className="flex h-16 min-h-16 w-16 min-w-[64px] items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-lg font-medium text-white shadow-lg">
                {getInitialsName(
                  clientDetailData?.first_name,
                  clientDetailData?.last_name
                )}
              </span>
            </Skeleton>
            <div className="flex w-full flex-col items-start justify-center gap-0.5">
              <Skeleton {...{ isLoading }} className="!w-2/3">
                <div className="flex items-center justify-start gap-2">
                  <h2 className="text-2xl font-bold">
                    {clientDetailData?.first_name} {clientDetailData?.last_name}
                  </h2>
                  <span className="rounded-xl bg-gray-200 px-2.5 pb-0.5 pt-[3px] text-xs font-medium text-gray-800">
                    {clientDetailData?.id}
                  </span>
                </div>
              </Skeleton>
              <Skeleton {...{ isLoading }} className="!w-1/3">
                <div className="flex items-center justify-start text-sm text-gray-500">
                  <span className="flex items-center justify-start gap-1">
                    <CalendarBlank /> DOB:{' '}
                    {dateOfBirth === 'Invalid date' ? 'Empty' : dateOfBirth}
                  </span>
                  {clientDetailData?.external_id ? (
                    <>
                      <span className="px-2">•</span>
                      <span>
                        <span className="text-gray-900">
                          {clientDetailData?.external_integration
                            ?.toLowerCase()
                            .split(' ')
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(' ')}{' '}
                          ID:
                        </span>{' '}
                        {clientDetailData?.external_id}
                      </span>
                    </>
                  ) : null}
                </div>
              </Skeleton>
              <Skeleton {...{ isLoading }} className="!w-2/4">
                <EditableBadge.Root maxVisible={4}>
                  {tagList?.map((item) => (
                    <EditableBadge.Item
                      badgeList={BadgeEditList}
                      color={item.color}
                      key={item.id}
                      onConfirmRemove={async () =>
                        await removeTagFromClient(item.id as string)
                      }
                      tagId={item.id as string}
                      title={item.name}
                    />
                  ))}
                  <AddBadgeButtonClient />
                </EditableBadge.Root>
              </Skeleton>
            </div>
            <div className="flex h-full w-[fit-content] items-center justify-start gap-4">
              <ClientHeaderSettings />
            </div>
          </div>
        </div>
      </div>

      {isOpenCreateTagModal ? <AddTagClientModal /> : null}
      {isOpenEditTagModal ? <EditTagClientModal /> : null}
      {isOpenDeleteTagModal ? <ConfirmDeleteTagClientModal /> : null}
    </>
  )
}
